
import { Prop, Component, Provide, Mixins } from "vue-property-decorator";

// Mixins
import Context from "@/mixins/context";

// Enums/Data
import { Contexts } from "@/models/contexts";
import { GrantTypes } from "@/data/enums/tokens";

// Typings
import {
  IClientAuthProviderData,
  IClientAuthProviderMethods,
  IClientAuthProviderForm
} from "@/models/providers/clientAuth";
import { ICurrency } from "@/models/constants";
import { IToken } from "@/models/token";

@Component
export default class ClientAuthProvider extends Mixins(Context) {
  // Props

  @Prop({ default: null })
  public currencyId!: ICurrency["id"];

  // Variables

  public form: IClientAuthProviderForm = {
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    custom_fields: {},
    recaptcha_token: ""
  };

  // Methods

  public registerClient() {
    return this.$store.dispatch("auth/client/register", {
      form: {
        ...this.form,
        ...(this.currencyId ? { currency_id: this.currencyId } : {})
      },
      accessToken: this.guestAccessToken
    });
  }

  public createAccount() {
    return this.$store.dispatch("auth/client/register", {
      form: this.form,
      accessToken: this.guestAccessToken
    });
  }

  public async registerGuest() {
    const clientResponse = await this.$store.dispatch("auth/guest/register", {
      currency_id: this.currencyId
    });

    const { token }: { token: IToken } = await this.$store.dispatch(
      "auth/createAccessToken",
      {
        data: {
          client_id: clientResponse.data.id,
          grant_type: GrantTypes.GUEST_CUSTOMER
        },
        withAccessToken: true
      }
    );
    await this.$store.dispatch("auth/saveToken", {
      token,
      context: Contexts.CLIENT
    });
  }

  public completeFullRegistration() {
    return this.$store.dispatch("auth/guest/completeFullRegistration", {
      clientId: this.$store.state.user.id,
      userData: this.$_.pick(this.form, [
        "firstname",
        "lastname",
        "email",
        "password",
        "custom_fields"
      ])
    });
  }

  public login() {
    return this.$store.dispatch("auth/login", {
      context: Contexts.CLIENT,
      payload: {
        username: this.form.email,
        password: this.form.password,
        grant_type: GrantTypes.PASSWORD
      }
    });
  }

  public scrollToFirstInput() {
    this.$nextTick(() => {
      const input = document.querySelector("input");
      if (!input) {
        return;
      }
      input.scrollIntoView({ behavior: "smooth" });
    });
  }

  // Provide

  @Provide("clientAuthProviderData")
  public providerData(): IClientAuthProviderData {
    return {
      form: this.form
    };
  }

  @Provide("clientAuthProviderMethods")
  public get providerMethods(): IClientAuthProviderMethods {
    return {
      registerClient: this.registerClient,
      registerGuest: this.registerGuest,
      completeFullRegistration: this.completeFullRegistration,
      login: this.login,
      scrollToFirstInput: this.scrollToFirstInput
    };
  }

  // Render

  public render() {
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $clientAuthData: this.providerData(),
        $clientAuthMethods: this.providerMethods
      })
    );
  }
}
